import { EXPORT_INCLUSION_TYPES } from "@/components/Export/types";
import {
  ProductPresenceDataType,
  VendorPresenceDataType,
  SelectableCustomColumn,
} from "./types";

/**
 * Serializable shape of a Spend Sheet configuration. This is the JSON object
 * HGers copy/share and load back into the export modal. It intentionally omits
 * the runtime-only `id`/`phase` fields of `SelectableCustomColumn` so the JSON
 * is stable and portable across sessions.
 */
export type SpendSheetConfigColumn = {
  type: "product" | "vendor";
  name: string;
  dataType: ProductPresenceDataType | VendorPresenceDataType;
  ids: number[];
  isSelected: boolean;
};

export type SpendSheetConfig = {
  version: 1;
  spendInclusion: EXPORT_INCLUSION_TYPES.ALL | EXPORT_INCLUSION_TYPES.NONE;
  cloudMaturityEnabled: boolean;
  customColumns: SpendSheetConfigColumn[];
};

const CONFIG_VERSION = 1 as const;

type SerializeInput = {
  spendInclusion: EXPORT_INCLUSION_TYPES;
  cloudMaturityEnabled: boolean;
  customColumns: SelectableCustomColumn[];
};

/**
 * Serialize the current Spend Sheet state into a pretty-printed JSON string.
 * Strips the runtime-only `id`/`phase` fields from each custom column.
 */
export const serializeSpendSheetConfig = ({
  spendInclusion,
  cloudMaturityEnabled,
  customColumns,
}: SerializeInput): string => {
  const config: SpendSheetConfig = {
    version: CONFIG_VERSION,
    spendInclusion:
      spendInclusion === EXPORT_INCLUSION_TYPES.NONE
        ? EXPORT_INCLUSION_TYPES.NONE
        : EXPORT_INCLUSION_TYPES.ALL,
    cloudMaturityEnabled,
    customColumns: customColumns.map((column) => ({
      type: column.type,
      name: column.name,
      dataType: column.dataType,
      ids: column.ids,
      isSelected: column.isSelected,
    })),
  };

  return JSON.stringify(config, null, 2);
};

export type ParseResult =
  | { ok: true; config: SpendSheetConfig }
  | { ok: false; error: string };

const VALID_PRODUCT_DATA_TYPES = new Set<string>(
  Object.values(ProductPresenceDataType)
);
const VALID_VENDOR_DATA_TYPES = new Set<string>(
  Object.values(VendorPresenceDataType)
);

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const parseColumn = (
  raw: unknown,
  index: number
):
  | { ok: true; column: SpendSheetConfigColumn }
  | { ok: false; error: string } => {
  if (!isRecord(raw)) {
    return {
      ok: false,
      error: `Custom column #${index + 1} must be an object.`,
    };
  }

  const { type, name, dataType, ids, isSelected } = raw;

  if (type !== "product" && type !== "vendor") {
    return {
      ok: false,
      error: `Custom column #${index + 1} has an invalid type "${String(type)}". Expected "product" or "vendor".`,
    };
  }

  if (typeof name !== "string" || name.trim() === "") {
    return {
      ok: false,
      error: `Custom column #${index + 1} must have a non-empty name.`,
    };
  }

  const validDataTypes =
    type === "product" ? VALID_PRODUCT_DATA_TYPES : VALID_VENDOR_DATA_TYPES;
  if (typeof dataType !== "string" || !validDataTypes.has(dataType)) {
    return {
      ok: false,
      error: `Custom column "${name}" has an invalid data type "${String(dataType)}" for a ${type} column.`,
    };
  }

  if (
    !Array.isArray(ids) ||
    !ids.every((id) => typeof id === "number" && Number.isFinite(id))
  ) {
    return {
      ok: false,
      error: `Custom column "${name}" must have a list of numeric ids.`,
    };
  }

  return {
    ok: true,
    column: {
      type,
      name,
      dataType: dataType as ProductPresenceDataType | VendorPresenceDataType,
      ids: ids as number[],
      // Default to selected when omitted so a pasted column is included by default.
      isSelected: typeof isSelected === "boolean" ? isSelected : true,
    },
  };
};

/**
 * Validate and normalize a JSON string into a `SpendSheetConfig`. Returns a
 * discriminated result so callers can surface a clear error message.
 */
export const parseSpendSheetConfig = (json: string): ParseResult => {
  if (json.trim() === "") {
    return {
      ok: false,
      error: "Configuration is empty. Paste a configuration first.",
    };
  }

  let raw: unknown;
  try {
    raw = JSON.parse(json);
  } catch {
    return { ok: false, error: "Invalid JSON. Please check the format." };
  }

  if (!isRecord(raw)) {
    return { ok: false, error: "Configuration must be a JSON object." };
  }

  const spendInclusion =
    raw.spendInclusion === EXPORT_INCLUSION_TYPES.NONE
      ? EXPORT_INCLUSION_TYPES.NONE
      : EXPORT_INCLUSION_TYPES.ALL;

  const cloudMaturityEnabled = raw.cloudMaturityEnabled === true;

  if (raw.customColumns !== undefined && !Array.isArray(raw.customColumns)) {
    return { ok: false, error: "`customColumns` must be an array." };
  }

  const rawColumns = Array.isArray(raw.customColumns) ? raw.customColumns : [];
  const customColumns: SpendSheetConfigColumn[] = [];
  for (let i = 0; i < rawColumns.length; i += 1) {
    const result = parseColumn(rawColumns[i], i);
    if (!result.ok) return result;
    customColumns.push(result.column);
  }

  return {
    ok: true,
    config: {
      version: CONFIG_VERSION,
      spendInclusion,
      cloudMaturityEnabled,
      customColumns,
    },
  };
};
